import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import {
  FiCalendar,
  FiClock,
  FiPlus,
  FiTrash2,
  FiSave,
  FiAlertCircle,
  FiX,
  FiChevronDown,
  FiInfo,
} from "react-icons/fi";
import { toast } from "react-toastify";
import appointment from "../../services/appointment";
import { useAuth } from "../../context/AuthContext";
import BlockedPeriodsSection from "./BlockedPeriodsSection";
import { BlockedPeriod } from "./BlockedPeriodItem";

interface Availability {
  id?: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active?: boolean;
}

const DAYS = [
  { value: 0, label: "Segunda-feira", short: "Seg" },
  { value: 1, label: "Terça-feira", short: "Ter" },
  { value: 2, label: "Quarta-feira", short: "Qua" },
  { value: 3, label: "Quinta-feira", short: "Qui" },
  { value: 4, label: "Sexta-feira", short: "Sex" },
  { value: 5, label: "Sábado", short: "Sáb" },
  { value: 6, label: "Domingo", short: "Dom" },
];

export interface AvailabilityTabHandle {
  save: () => Promise<void>;
}

interface AvailabilityTabProps {
  hideIndicators?: boolean;
  hideSaveButton?: boolean;
  onStateChange?: (state: {
    hasChanges: boolean;
    saving: boolean;
    hasErrors: boolean;
  }) => void;
}

const AvailabilityTab = forwardRef<AvailabilityTabHandle, AvailabilityTabProps>(
  function AvailabilityTab(
    { hideIndicators = false, hideSaveButton = false, onStateChange },
    ref,
  ) {
  const { getUser } = useAuth();
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [blockedPeriods, setBlockedPeriods] = useState<BlockedPeriod[]>([]);
  const [autoBlockScheduled, setAutoBlockScheduled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingBlocked, setSavingBlocked] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasChangesBlocked, setHasChangesBlocked] = useState(false);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [errorsBlocked, setErrorsBlocked] = useState<Record<number, string>>(
    {},
  );
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  useEffect(() => {
    loadAvailabilities();
    loadBlockedPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailabilities = async () => {
    const user = getUser();
    if (!user?.id) return;

    setLoading(true);
    try {
      const data = await appointment.getAvailability(String(user.id));
      setAvailabilities(data.availabilities || []);
      setAutoBlockScheduled(data.auto_block_scheduled ?? true);
      setHasChanges(false);
    } catch (error) {
      console.error("Erro ao carregar disponibilidades:", error);
      toast.error("Erro ao carregar disponibilidades");
    } finally {
      setLoading(false);
    }
  };

  const loadBlockedPeriods = async () => {
    const user = getUser();
    if (!user?.id) return;

    try {
      const data = await appointment.getBlockedPeriods(String(user.id));
      setBlockedPeriods(data || []);
      setHasChangesBlocked(false);
    } catch (error) {
      console.error("Erro ao carregar períodos bloqueados:", error);
    }
  };

  const validateTime = (
    index: number,
    start: string,
    end: string,
  ): string | null => {
    if (!start || !end) {
      return "Horários de início e fim são obrigatórios";
    }

    const startMinutes =
      parseInt(start.split(":")[0]) * 60 + parseInt(start.split(":")[1]);
    const endMinutes =
      parseInt(end.split(":")[0]) * 60 + parseInt(end.split(":")[1]);

    if (endMinutes <= startMinutes) {
      return "O horário de fim deve ser posterior ao horário de início";
    }

    const currentDay = availabilities[index].day_of_week;
    const conflicts = availabilities.filter(
      (a, i) =>
        i !== index &&
        a.day_of_week === currentDay &&
        ((startMinutes >=
          parseInt(a.start_time.split(":")[0]) * 60 +
            parseInt(a.start_time.split(":")[1]) &&
          startMinutes <
            parseInt(a.end_time.split(":")[0]) * 60 +
              parseInt(a.end_time.split(":")[1])) ||
          (endMinutes >
            parseInt(a.start_time.split(":")[0]) * 60 +
              parseInt(a.start_time.split(":")[1]) &&
            endMinutes <=
              parseInt(a.end_time.split(":")[0]) * 60 +
                parseInt(a.end_time.split(":")[1])) ||
          (startMinutes <=
            parseInt(a.start_time.split(":")[0]) * 60 +
              parseInt(a.start_time.split(":")[1]) &&
            endMinutes >=
              parseInt(a.end_time.split(":")[0]) * 60 +
                parseInt(a.end_time.split(":")[1]))),
    );

    if (conflicts.length > 0) {
      return "Este horário conflita com outro horário no mesmo dia";
    }

    return null;
  };

  const handleAdd = () => {
    const newAvailability = {
      day_of_week: 0,
      start_time: "09:00",
      end_time: "18:00",
      is_active: true,
    };
    setAvailabilities([...availabilities, newAvailability]);
    setHasChanges(true);
  };

  const handleRemove = (index: number) => {
    setAvailabilities(availabilities.filter((_, i) => i !== index));
    setHasChanges(true);
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleChange = (
    index: number,
    field: keyof Availability,
    value: string | number,
  ) => {
    const updated = [...availabilities];
    updated[index] = { ...updated[index], [field]: value };
    setAvailabilities(updated);
    setHasChanges(true);

    if (
      field === "start_time" ||
      field === "end_time" ||
      field === "day_of_week"
    ) {
      const error = validateTime(
        index,
        field === "start_time" ? (value as string) : updated[index].start_time,
        field === "end_time" ? (value as string) : updated[index].end_time,
      );
      const newErrors = { ...errors };
      if (error) {
        newErrors[index] = error;
      } else {
        delete newErrors[index];
      }
      setErrors(newErrors);
    }
  };

  const handleSave = useCallback(async () => {
    const user = getUser();
    if (!user?.id) return;

    const newErrors: Record<number, string> = {};
    availabilities.forEach((availability, index) => {
      const error = validateTime(
        index,
        availability.start_time,
        availability.end_time,
      );
      if (error) {
        newErrors[index] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Corrija os erros antes de salvar");
      return;
    }

    setSaving(true);
    try {
      const result = await appointment.setAvailability(
        String(user.id),
        availabilities,
        autoBlockScheduled,
      );
      if (result) {
        toast.success("Disponibilidade salva com sucesso!");
        await loadAvailabilities();
      } else {
        toast.error("Erro ao salvar disponibilidade");
      }
    } catch (error) {
      console.error("Erro ao salvar disponibilidades:", error);
      toast.error("Erro ao salvar disponibilidade");
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUser, availabilities, autoBlockScheduled]);

  const handleCancel = () => {
    loadAvailabilities();
    setErrors({});
  };

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  useEffect(() => {
    onStateChange?.({
      hasChanges,
      saving,
      hasErrors: Object.keys(errors).length > 0,
    });
  }, [hasChanges, saving, errors, onStateChange]);

  const handleAddBlocked = () => {
    const today = new Date().toISOString().split("T")[0];
    const newBlockedPeriod: BlockedPeriod = {
      start_date: today,
      end_date: today,
      reason: "",
      is_active: true,
    };
    setBlockedPeriods([...blockedPeriods, newBlockedPeriod]);
    setHasChangesBlocked(true);
  };

  const handleRemoveBlocked = (index: number) => {
    setBlockedPeriods(blockedPeriods.filter((_, i) => i !== index));
    setHasChangesBlocked(true);
    const newErrors = { ...errorsBlocked };
    delete newErrors[index];
    setErrorsBlocked(newErrors);
  };

  const handleChangeBlocked = (
    index: number,
    field: keyof BlockedPeriod,
    value: string,
  ) => {
    const updated = [...blockedPeriods];
    updated[index] = { ...updated[index], [field]: value };
    setBlockedPeriods(updated);
    setHasChangesBlocked(true);

    if (field === "start_date" || field === "end_date") {
      const period = updated[index];
      if (period.start_date && period.end_date) {
        const startDate = new Date(period.start_date);
        const endDate = new Date(period.end_date);
        if (endDate < startDate) {
          const newErrors = { ...errorsBlocked };
          newErrors[index] =
            "A data final deve ser posterior ou igual à data inicial";
          setErrorsBlocked(newErrors);
        } else {
          const newErrors = { ...errorsBlocked };
          delete newErrors[index];
          setErrorsBlocked(newErrors);
        }
      }
    }
  };

  const handleSaveBlocked = async () => {
    const user = getUser();
    if (!user?.id) return;

    const newErrors: Record<number, string> = {};
    blockedPeriods.forEach((period, index) => {
      if (!period.start_date || !period.end_date) {
        newErrors[index] = "Data de início e fim são obrigatórias";
      } else {
        const startDate = new Date(period.start_date);
        const endDate = new Date(period.end_date);
        if (endDate < startDate) {
          newErrors[index] =
            "A data final deve ser posterior ou igual à data inicial";
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrorsBlocked(newErrors);
      toast.error("Corrija os erros antes de salvar");
      return;
    }

    setSavingBlocked(true);
    try {
      const result = await appointment.setBlockedPeriods(
        String(user.id),
        blockedPeriods,
      );
      if (result) {
        toast.success("Períodos bloqueados salvos com sucesso!");
        await loadBlockedPeriods();
      } else {
        toast.error("Erro ao salvar períodos bloqueados");
      }
    } catch (error) {
      console.error("Erro ao salvar períodos bloqueados:", error);
      toast.error("Erro ao salvar períodos bloqueados");
    } finally {
      setSavingBlocked(false);
    }
  };

  const handleCancelBlocked = () => {
    loadBlockedPeriods();
    setErrorsBlocked({});
  };

  const getDurationDays = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const getDayColor = (dayIndex: number) => {
    const colors = [
      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
      "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
      "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    ];
    return colors[dayIndex];
  };

  const InfoTooltip = ({ id, text }: { id: string; text: string }) => (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setTooltipVisible(id)}
        onMouseLeave={() => setTooltipVisible(null)}
        onClick={() => setTooltipVisible(tooltipVisible === id ? null : id)}
        className="ml-2 inline-flex items-center justify-center rounded-full text-gray-400 transition hover:text-purple-600 dark:hover:text-purple-400"
      >
        <FiInfo size={16} />
      </button>
      {tooltipVisible === id && (
        <div className="absolute left-0 top-6 z-50 w-72 rounded-lg border border-stroke bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-lg dark:border-strokedark dark:bg-boxdark dark:text-gray-300">
          {text}
          <div className="absolute -top-1 left-4 h-2 w-2 rotate-45 border-l border-t border-stroke bg-white dark:border-strokedark dark:bg-boxdark" />
        </div>
      )}
    </div>
  );

  const groupedByDay = availabilities.reduce(
    (acc, availability, index) => {
      const day = availability.day_of_week;
      if (!acc[day]) acc[day] = [];
      acc[day].push({ ...availability, originalIndex: index });
      return acc;
    },
    {} as Record<number, (Availability & { originalIndex: number })[]>,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <div className="flex items-center">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white sm:text-xl">
              Gerenciar Disponibilidade
            </h3>
            <InfoTooltip
              id="availability-info"
              text="Configure os horários em que você estará disponível para receber agendamentos. Você pode criar múltiplos períodos para cada dia da semana."
            />
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400 sm:mt-2 sm:text-sm">
            Configure os horários em que você está disponível para agendamentos
          </p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:w-auto sm:px-5"
        >
          <FiPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
          Adicionar Horário
        </button>
      </div>

      {/* Resumo visual */}
      {!hideIndicators && availabilities.length > 0 && (
        <div className="rounded-lg border border-stroke bg-gradient-to-br from-purple-50 to-blue-50 p-4 shadow-sm dark:border-strokedark dark:from-purple-900/10 dark:to-blue-900/10 sm:rounded-xl sm:p-6">
          <div className="mb-3 flex items-center sm:mb-4">
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 sm:text-sm">
              Resumo Semanal
            </h4>
            <InfoTooltip
              id="weekly-summary"
              text="Visão geral rápida mostrando quantos períodos de disponibilidade você tem configurado para cada dia da semana."
            />
          </div>
          <div className="grid grid-cols-4 gap-2 sm:gap-3 md:grid-cols-4 lg:grid-cols-7">
            {DAYS.map((day) => {
              const dayAvailabilities = groupedByDay[day.value] || [];
              return (
                <div
                  key={day.value}
                  className={`rounded-lg p-2.5 text-center transition-all sm:rounded-xl sm:p-3 ${
                    dayAvailabilities.length > 0
                      ? getDayColor(day.value) + " shadow-sm"
                      : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600"
                  }`}
                >
                  <div className="text-[10px] font-semibold uppercase tracking-wide sm:text-xs">
                    {day.short}
                  </div>
                  <div className="mt-1.5 text-xl font-bold sm:mt-2 sm:text-2xl">
                    {dayAvailabilities.length || "-"}
                  </div>
                  <div className="mt-0.5 text-[9px] opacity-75 sm:mt-1 sm:text-[10px]">
                    {dayAvailabilities.length === 1 ? "período" : "períodos"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lista de disponibilidades */}
      {availabilities.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-10 text-center dark:border-gray-700 dark:bg-gray-800/50 sm:rounded-xl sm:p-16">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 sm:mb-4 sm:h-20 sm:w-20">
            <FiCalendar
              size={32}
              className="text-purple-600 dark:text-purple-400 sm:w-10 sm:h-10"
            />
          </div>
          <h4 className="mb-2 text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
            Nenhum horário configurado
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
            Clique em "Adicionar Horário" para começar a configurar sua
            disponibilidade
          </p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {availabilities.map((availability, index) => (
            <div
              key={index}
              className={`rounded-lg border bg-white p-4 shadow-sm transition-all dark:bg-boxdark sm:rounded-xl sm:p-6 ${
                errors[index]
                  ? "border-red-300 ring-2 ring-red-100 dark:border-red-800 dark:ring-red-900/30"
                  : "border-stroke dark:border-strokedark hover:shadow-md"
              }`}
            >
              <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-12 lg:items-start">
                {/* Dia da Semana */}
                <div className="lg:col-span-3 min-w-0">
                  <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                    Dia da Semana
                  </label>
                  <div className="relative">
                    <select
                      value={availability.day_of_week}
                      onChange={(e) =>
                        handleChange(
                          index,
                          "day_of_week",
                          parseInt(e.target.value),
                        )
                      }
                      className="w-full appearance-none rounded-lg border border-stroke bg-transparent px-3 py-2.5 pr-9 text-xs outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:ring-purple-800 sm:px-4 sm:py-3 sm:pr-10 sm:text-sm"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                      }}
                    >
                      {DAYS.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <FiChevronDown
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 sm:right-4"
                      size={16}
                    />
                  </div>
                </div>

                {/* Horário Início */}
                <div className="lg:col-span-3 min-w-0">
                  <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                    Horário Início
                  </label>
                  <div className="relative overflow-hidden">
                    <FiClock
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                      size={14}
                    />
                    <input
                      type="time"
                      value={availability.start_time}
                      onChange={(e) =>
                        handleChange(index, "start_time", e.target.value)
                      }
                      className="w-full max-w-full box-border rounded-lg border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-xs outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:border-strokedark dark:bg-form-input dark:focus:ring-purple-800 sm:py-3 sm:pl-11 sm:pr-4 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Horário Fim */}
                <div className="lg:col-span-3 min-w-0">
                  <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                    Horário Fim
                  </label>
                  <div className="relative overflow-hidden">
                    <FiClock
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                      size={14}
                    />
                    <input
                      type="time"
                      value={availability.end_time}
                      onChange={(e) =>
                        handleChange(index, "end_time", e.target.value)
                      }
                      className="w-full max-w-full box-border rounded-lg border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-xs outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:border-strokedark dark:bg-form-input dark:focus:ring-purple-800 sm:py-3 sm:pl-11 sm:pr-4 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Duração e Ações */}
                <div className="lg:col-span-3 min-w-0">
                  <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                    Duração
                  </label>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0 rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-center text-xs font-semibold text-gray-700 dark:border-strokedark dark:bg-gray-800 dark:text-gray-300 sm:px-4 sm:py-3 sm:text-sm">
                      {(() => {
                        const start = availability.start_time.split(":");
                        const end = availability.end_time.split(":");
                        const diff =
                          parseInt(end[0]) * 60 +
                          parseInt(end[1]) -
                          (parseInt(start[0]) * 60 + parseInt(start[1]));
                        const hours = Math.floor(diff / 60);
                        const mins = diff % 60;
                        return `${hours}h${mins > 0 ? " " + mins + "min" : ""}`;
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="rounded-lg border border-red-300 bg-red-50 p-2.5 text-red-700 transition-all hover:bg-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 sm:p-3"
                      title="Remover horário"
                    >
                      <FiTrash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Mensagem de erro */}
              {errors[index] && (
                <div className="mt-3 flex items-start gap-2.5 rounded-lg bg-red-50 p-3 dark:bg-red-900/20 sm:mt-4 sm:gap-3 sm:p-4">
                  <FiAlertCircle
                    className="mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400"
                    size={16}
                  />
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 sm:text-sm">
                    {errors[index]}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Checkbox para bloquear automaticamente */}
      <div className="rounded-lg border border-stroke bg-white p-4 shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-6">
        <label className="flex cursor-pointer items-start gap-3 sm:gap-4">
          <input
            type="checkbox"
            checked={autoBlockScheduled}
            onChange={(e) => {
              setAutoBlockScheduled(e.target.checked);
              setHasChanges(true);
            }}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-purple-600 transition focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-800 sm:mt-1 sm:h-5 sm:w-5"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-800 dark:text-white sm:text-base">
              Bloquear horário automaticamente mesmo sem confirmação
            </div>
            <div className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400 sm:mt-1.5 sm:text-sm">
              Com esta opção ativa, horários são bloqueados imediatamente ao
              agendar. Se desativada, só são bloqueados após confirmação.
            </div>
          </div>
        </label>
      </div>

      {/* Botões de ação */}
      {!hideSaveButton && availabilities.length > 0 && hasChanges && (
        <div className="flex flex-col-reverse gap-2.5 border-t border-stroke pt-4 sm:flex-row sm:gap-3 sm:justify-end sm:pt-6 dark:border-strokedark">
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 dark:border-strokedark dark:bg-boxdark dark:text-gray-300 dark:hover:bg-meta-4 sm:px-6 sm:py-3"
          >
            <FiX size={16} className="sm:w-[18px] sm:h-[18px]" />
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || Object.keys(errors).length > 0}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-3"
          >
            {saving ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Salvando...
              </>
            ) : (
              <>
                <FiSave size={16} className="sm:w-[18px] sm:h-[18px]" />
                Salvar Disponibilidade
              </>
            )}
          </button>
        </div>
      )}

      <div className="border-t border-stroke dark:border-strokedark" />

      {/* Seção de Períodos Bloqueados */}
      <BlockedPeriodsSection
        blockedPeriods={blockedPeriods}
        errorsBlocked={errorsBlocked}
        savingBlocked={savingBlocked}
        hasChangesBlocked={hasChangesBlocked}
        onAdd={handleAddBlocked}
        onChange={handleChangeBlocked}
        onRemove={handleRemoveBlocked}
        onSave={handleSaveBlocked}
        onCancel={handleCancelBlocked}
        getDurationDays={getDurationDays}
      />

      {/* Indicador de mudanças não salvas */}
      {!hideIndicators && (hasChanges || hasChangesBlocked) && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 shadow-xl dark:border-yellow-800 dark:bg-yellow-900/30 sm:bottom-6 sm:right-6 sm:rounded-xl sm:px-5 sm:py-4">
          <div className="flex items-center gap-2.5 text-xs font-medium text-yellow-800 dark:text-yellow-300 sm:gap-3 sm:text-sm">
            <FiAlertCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
            Você tem alterações não salvas
          </div>
        </div>
      )}
    </div>
  );
  },
);

export default AvailabilityTab;
